import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { useEffect, useState, useRef } from "react";
import { Tree } from "primereact/tree";
import TreeNode from "primereact/treenode";
import { TabView, TabPanel } from "primereact/tabview";
import { useForm } from "../../../../../shared/hooks/Form";
import { GroupPermissionService as PermissionService } from "./GroupPermissionService";
import { GroupMPermissionService as MPermissionService } from "./GroupMPermissionService";
import { RoleService as RoleService } from "../RoleService";
import { ToastService } from "../../../.././../AppToast";
import { CONSTANT } from "../../../../../Constant";
import { useTranslation } from "../../../../../shared/language";

export const useDialogCreate = (props: CreateProps) => {
  const dataKey = "id";
  const { t } = useTranslation();
  const header = t('system.permissionGroups');
  const PERMISSION_LABEL = t('system.permissions');
  const ROOT_NODE_NAME = CONSTANT.ROOT_NODE_NAME;
  const OPEN_ICON = "pi pi-fw pi-folder-open";
  const CLOSE_ICON = "pi pi-fw pi-folder";
  const NO_ACTIVE = 0;
  const PARTIAL_ACTIVE = 1;
  const ACTIVE_ALL = 2;
  const TAB_KEY_CODE = 9;

  const { group, reloadLazyData } = props;
  const [idUpdate, setIdUpdate] = useState(null);
  const [isEditable, setIsEditable] = useState(false);
  const [roleName, setRoleName] = useState(null);
  const nodes = useRef([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [selectedTreeNode, setSelectedTreeNode] = useState(null);
  const permissions = useRef([]);
  const rawSelectKey = useRef({});
  const [display, setDisplay] = useState(false);
  const [width, setWidth] = useState(700);
  const [submitting, setSubmitting] = useState(false);
  const [providerKeyOld, setProviderKeyOld] = useState('');
  const [expandedKeys, setExpandedKeys] = useState({});

  useEffect(() => {
    if (display) {
      setActiveIndex(0);
      if (roleName) {
        PermissionService.currentTenant().then(async (res) => {
          let root: TreeNode = {
            key: ROOT_NODE_NAME,
            icon: CLOSE_ICON,
            label: ROOT_NODE_NAME,
            data: ROOT_NODE_NAME,
            children: [],
          };
          permissions.current = res;
          await getTreeRecur(res, root);
          nodes.current = root.children;
          MPermissionService.getByName(roleName).then(async (res) => {
            setProviderKeyOld(roleName);
            let mPermision = res.groups.find((t) => t.displayName === ROOT_NODE_NAME);
            rawSelectKey.current = {};
            if (mPermision) {
              let grantPermission = mPermision.permissions.filter((p) => p.isGranted);
              grantPermission.forEach((t) => {
                let curStatus = {};
                curStatus[t.name] = { checked: true, partialChecked: false };
                rawSelectKey.current = Object.assign(rawSelectKey.current, curStatus);
              });
              await updateTreeState(root);

              setSelectedTreeNode(rawSelectKey.current);
              updateSelectedPermission(rawSelectKey.current)
              collapseAll();
            }
          });
        });
      } else {
        PermissionService.currentTenant().then(async (res) => {
          let root: TreeNode = {
            key: ROOT_NODE_NAME,
            icon: CLOSE_ICON,
            label: ROOT_NODE_NAME,
            data: ROOT_NODE_NAME,
            children: [],
          };
          permissions.current = res;
          rawSelectKey.current = {};
          await getTreeRecur(res, root);
          nodes.current = root.children;
          await updateTreeState(root);
          setSelectedTreeNode(rawSelectKey.current);
          collapseAll();
        });
      }
    }
  }, [display]);

  useEffect(() => {
    window.addEventListener("keydown", keyDownHandler);

    return () => {
      window.removeEventListener("keydown", keyDownHandler);
    };
  }, []);

  useEffect(() => {
    if(idUpdate){
      setIsEditable(true);
    }else{
      setIsEditable(false);
    }
  }, [idUpdate]);

  const {
    render: renderForm,
    reset,
    setValue,
    valid: formValid,
    getValue,
  } = useForm({
    fields: [{ field: "name", header: t('system.permissionGroups'), required: true, InputTextProps: { maxLength: 250 }, className: "md:col-12" }],
    idUpdate,
  });

  const handleSubmit = () => {
    if (!submitting && formValid()) {
      setSubmitting(true);
      let promise;

      if (idUpdate) {
        promise = submitPermissions(getValue());
      } else {
        promise = createPermissions(getValue());
      }

      promise
        .then((data) => {
          setDisplay(false);
          ToastService.success();
          reloadLazyData(data);
        })
        .finally(() => {
          setSubmitting(false);
        });
    }
  };

  const createPermissions = (data) => {
    let updatedPermissions = permissions.current.map((t) => {
      let permission = { name: t.name, isGranted: t.isGranted };
      return permission;
    });
    let payload = { 
      providerName: 'R',
      role: {
        name: data.name,
        isDefault: false,
        isPublic: true
      },
      permissions: {permissions: updatedPermissions} 
    };
    return MPermissionService.createPermission(payload);
  };

  const submitPermissions = (data) => {
    let updatedPermissions = permissions.current.map((t) => {
      let permission = { name: t.name, isGranted: t.isGranted };
      return permission;
    });
    let payload = { permissions: updatedPermissions };
    return MPermissionService.updateRole(data.name, providerKeyOld, payload);
  };

  const show = () => {
    setDisplay(true);
  };

  const create = () => {
    setIdUpdate(null);
    setValue({});
    setRoleName(null);
    setDisplay(true);
  };

  const copy = (item) => {
    setIdUpdate(null);
    setValue(item);
    setRoleName(item.name);
    setDisplay(true);
  };

  const update = (item: any) => {
    setValue(item);
    setIdUpdate(item.id);
    setRoleName(item.name);
    setDisplay(true);
  };

  const selectKey = (data) => {
  };

  const unSelectKey = (data) => {
  };

  const onSelectionChange = async (data) => {
    setSelectedTreeNode(data);
    updateSelectedPermission(data);
  };

  const onCollapse = (node) => {
    node.icon = CLOSE_ICON;
  };

  const onExpand = (node) => {
    node.icon = OPEN_ICON;
  };

  const getTreeRecur = (data: RoleNode[], parentNode: TreeNode) => {
    let childs = data
      .filter((t) => t.parentName == parentNode.key)
      .map((item) => {
        let data: TreeNode = {
          key: item.name,
          icon: CLOSE_ICON,
          label: item.displayName,
          data: item.name,
          children: [],
        };
        return data;
      });

    parentNode.children = childs;
    if (childs.length) {
      childs.forEach((element) => {
        getTreeRecur(data, element);
      });
    }
  };

  const selectChild = (node) => {
    node.icon = OPEN_ICON;
    if (node.children?.length > 0) {
      node.children.forEach((element) => {
        selectChild(element);
      });
    }
  };

  const unSelectChild = (node) => {
    node.icon = CLOSE_ICON;
    if (node.children?.length > 0) {
      node.children.forEach((element) => {
        selectChild(unSelectChild);
      });
    }
  };

  const updateCurNode = (data, curNode, isSelected) => {
    if (data.key == curNode.key) {
      if (isSelected) {
        selectChild(data);
      } else {
        unSelectChild(data);
      }
    } else if (data.children?.length) {
      updateCurNode(data, curNode, isSelected);
    }
  };

  const expandAll = () => {
    let _expandedKeys = {};
    for (let node of nodes.current) {
      expandNode(node, _expandedKeys);
    }

    setExpandedKeys(_expandedKeys);
  };

  const collapseAll = () => {
    setExpandedKeys({});
  };

  const expandNode = (node, _expandedKeys) => {
    if (!node.children || !node.children.length) {
      return;
    }

    _expandedKeys[node.key] = true;
    for (let child of node.children) {
      expandNode(child, _expandedKeys);
    }
  };

  const keyDownHandler = (event: any) => {
    if (!display) return;
    if (event.keyCode === TAB_KEY_CODE) {
      event.preventDefault();
      event.stopPropagation();
      expandAll();
    }
  };

  const updateTreeState = async (node: TreeNode) => {
    let numChild = node.children ? node.children.length : 0;
    let numActiveChild = 0;
    let isActive = rawSelectKey.current.hasOwnProperty(node.key);

    if (isActive) {
      return ACTIVE_ALL;
    } else if (numChild === 0) {
      return NO_ACTIVE;
    }

    for (let i = 0; i < node.children.length; i++) {
      let childStatus = await updateTreeState(node.children[i]);
      numActiveChild += childStatus;
    }

    if (numChild * ACTIVE_ALL === numActiveChild) {
      let curStatus = {};
      curStatus[node.key] = { checked: true, partialChecked: false };
      rawSelectKey.current = Object.assign(rawSelectKey.current, curStatus);
      return ACTIVE_ALL;
    } else if (numActiveChild > 0) {
      let curStatus = {};
      curStatus[node.key] = { checked: false, partialChecked: true };
      rawSelectKey.current = Object.assign(rawSelectKey.current, curStatus);
      return PARTIAL_ACTIVE;
    } else {
      return NO_ACTIVE;
    }
  };

  const updateSelectedPermission = async (selectedPermission) => {
    let curPermission = [];
    permissions.current.map((t) => (t.isGranted = false));
    Object.keys(selectedPermission).forEach((key) => {
      let value = selectedPermission[key];
      if (value.checked) {
        permissions.current.filter((t) => t.name == key).map((t) => (t.isGranted = true));
      }
    });

    return curPermission;
  };

  const render = () => {
    let _header = `${t('common.create')} ${header}`;
    if (idUpdate) {
      _header = `${t('common.edit')} ${header}`;
    }

    return (
      <Dialog header={_header} visible={display} style={{ width }} position="top" onHide={() => setDisplay(false)}>
        <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
          <TabPanel header={header}>{renderForm()}</TabPanel>
          <TabPanel header={PERMISSION_LABEL}>
            <div tabIndex={-1}>
              <Tree
                className="role-tree"
                value={nodes.current}
                selectionMode="checkbox"
                selectionKeys={selectedTreeNode}
                onSelectionChange={(e) => onSelectionChange(e.value)}
                onSelect={(e) => selectKey(e.node)}
                onUnselect={(e) => unSelectKey(e.node)}
                onCollapse={(e) => onCollapse(e.node)}
                onExpand={(e) => onExpand(e.node)}
                expandedKeys={expandedKeys}
                onToggle={(e) => setExpandedKeys(e.value)}
              />
            </div>
          </TabPanel>
        </TabView>
        <div className="flex justify-content-end">
          <Button label={t('common.close')} icon="pi pi-times" onClick={() => setDisplay(false)} className="p-button-text p-button-sm" />
          <Button label={t('common.save')} icon="pi pi-check" onClick={handleSubmit} loading={submitting} className="p-button-sm" />
        </div>
      </Dialog>
    );
  };

  return { render, show, update, create, copy };
};

interface CreateProps {
  group?: any;
  reloadLazyData?: any;
}

interface RoleNode {
  displayName: string;
  name: string;
  urlHelp: string;
  parentName: string;
  isTenantDefault: boolean;
  childs?: RoleNode[];
}
